import { useEffect, useRef, useState } from "react";

const clamp = (n, min = 0, max = 100) => Math.max(min, Math.min(max, n));

const useUploadingProgress = ({ company }) => {
  const [overall, setOverall] = useState(0); // 0..100 monotonic
  const [perFile, setPerFile] = useState(0); // 0..100 for current file (optional UI)
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState(null);
  const wsRef = useRef(null);
  // single ref to avoid accidental reassignment
  const refs = useRef({
    completed: 0, // fully finished files
    lastPerFile: 0, // last per-file % seen
  });

  // reset on new batch/company or when expected file count changes
  useEffect(() => {
    refs.current.completed = 0;
    refs.current.lastPerFile = 0;
    setOverall(0);
    setPerFile(0);
    setCount(0);
    setMessage(null)
  }, [company?.id, count]);

  useEffect(() => {
    if (!company) return;

    const ws = new WebSocket(
      `${process.env.REACT_APP_WSS_BASE_URL}_uploading_progress/${company.id}`
    );
    wsRef.current = ws;

    ws.onmessage = e => {
      const data = JSON.parse(e.data)
      const p = clamp(Number(data.progress) || 0); // per-file 0..100
      setMessage(data.message || null)
      setPerFile(p);

      // transition to 100 => count file once
      if (p === 100 && refs.current.lastPerFile !== 100) {
        refs.current.completed = Math.min(refs.current.completed + 1, count);
      }

      // aggregate overall; avoid double-counting 100 by using 0 contribution when p==100
      const currentContribution = p === 100 ? 0 : p;

      const rawOverall =
        count > 0
          ? ((refs.current.completed * 100 + currentContribution) /
              (count * 100)) *
            100
          : p; // if count not provided, mirror per-file (fallback)

      // clamp + round; enforce monotonic increase
      setOverall(prev => Math.max(prev, Math.round(clamp(rawOverall))));

      refs.current.lastPerFile = p;
    };

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [company, count]);

  // Use "overall" in your progress bar; "perFile" is optional
  return { progress: overall, perFile, message, setProgress: setOverall, setCount, setMessage };
};

export default useUploadingProgress;
